import { Navigate } from 'react-router-dom';
import { ReactNode } from 'react';
import { usePermissions } from '../../hooks/usePermissions';
import type { Action, Module } from '../../types/permissions';

interface PermissionGuardProps {
  /** Módulo a verificar (debe existir en la tabla `permissions`). */
  module: Module;
  /** Acción a verificar. Default: 'view'. */
  action?: Action;
  /** Ruta a la que redirigir si no tiene permiso. Default: '/acceso-denegado'. */
  redirectTo?: string;
  children: ReactNode;
}

/**
 * Guardia de permisos: renderiza children solo si el usuario tiene el permiso
 * (module, action) en la empresa activa. Si no, redirige.
 *
 * Mientras cargan los permisos no decide (evita flashes de redirección).
 *
 * Uso:
 *   <PermissionGuard module="financial" action="view"><GastosPage/></PermissionGuard>
 */
const PermissionGuard = ({
  module,
  action = 'view',
  redirectTo = '/acceso-denegado',
  children,
}: PermissionGuardProps) => {
  const { can, permissionsLoading } = usePermissions();

  if (permissionsLoading) {
    // No decidir hasta tener los permisos. Los componentes bajo guard suelen
    // estar envueltos en Layout, que ya muestra un estado de carga general.
    return null;
  }

  if (!can(module, action)) {
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
};

export default PermissionGuard;
